import { isNil } from "lodash";

import { chosenSites, chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import { NO_DATA_SIGN } from "app/filters/const";
import { roundNumberFilter } from "app/filters/ngFilters";
import { DEFAULT_DECIMAL, FORMATTERS, PERCENTAGE } from "app/common/benchmark/formatters";
import type { ISimilarInfoItem } from "app/services/sitesService/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { calculateChangeFromLastMonth } from "../../common/utils";
import { calculatePercentage } from "../components/SearchOverviewCarousel/data/utils";
import type { BrandedSplitData, SearchKeywordsData } from "../types";

export const getProgressBarValue = (part: number, total: number) => {
    if (isNil(total) || isNil(part)) {
        return;
    }

    const percentage = calculatePercentage(part, total);
    const hasValue = !isNil(percentage);

    return {
        formattedValue: hasValue ? `${percentage}${PERCENTAGE}` : NO_DATA_SIGN,
        numericValue: hasValue ? percentage : null,
    };
};
export const getKeywordsData = (data: {
    RequestedInterval: number;
    YearlyComparisonInterval: number;
}): SearchKeywordsData => {
    const changeOfKeywords =
        data.RequestedInterval && data.YearlyComparisonInterval
            ? calculateChangeFromLastMonth({
                  previousMonthValue: data.YearlyComparisonInterval,
                  currentMonthValue: data.RequestedInterval,
              }) / 100
            : undefined;

    const numOfKeywords = !isNil(data.RequestedInterval) ? FORMATTERS.numberAbbr(data.RequestedInterval) : undefined;
    return {
        numOfKeywords,
        changeOfKeywords,
    };
};
export const getDomainsData = (): Record<string, { name: string; url: string; favicon: string }> => {
    const state = proNavigator.getState(proNavigator.current());
    const params = proNavigator.getParams();

    return Object.fromEntries(
        chosenSites.sitelistForLegend().map(({ name, icon }: { name: string; icon: string }) => [
            name,
            {
                name,
                url: proNavigator.href(state, { ...params, key: name }),
                favicon: icon,
            },
        ]),
    );
};
export const getDomainsDataFromKeys = (
    domainsKeys: string,
    similarSites: ISimilarInfoItem[],
): Record<string, { name: string; url: string; favicon: string }> => {
    const state = proNavigator.getState(proNavigator.current());
    const params = proNavigator.getParams();
    const domainNames = domainsKeys.split(",");

    return Object.fromEntries(
        domainNames.map((name) => {
            const similarSite = similarSites.find((site) => site.Domain === name);
            return [
                name,
                {
                    name,
                    url: proNavigator.href(state, { ...params, key: name }),
                    favicon: similarSite?.Favicon || chosenSites.getInfo(name)?.icon || "",
                },
            ];
        }),
    );
};
export const formatChartData = (value: { BrandedShare: number; NonBrandedShare: number }): BrandedSplitData => {
    if (isNil(value?.BrandedShare) || isNil(value?.NonBrandedShare)) {
        return {
            branded: null,
            nonBranded: null,
        };
    }
    const { BrandedShare, NonBrandedShare } = value;
    return {
        branded: {
            numericValue: BrandedShare,
            formattedValue: FORMATTERS.percentSign(BrandedShare, DEFAULT_DECIMAL) as string,
        },
        nonBranded: {
            numericValue: NonBrandedShare,
            formattedValue: FORMATTERS.percentSign(NonBrandedShare, DEFAULT_DECIMAL) as string,
        },
    };
};
export const processRankPositionData = (domainData: {
    Top3: {
        [key: string]: number;
    };
    RestTo100: {
        [key: string]: number;
    };
}) => {
    const { Top3 = {}, RestTo100 = {} } = domainData;
    const sortedMonths = Object.keys(Top3).sort();
    if (sortedMonths.length < 2) {
        return null;
    }

    const [currentMonth, previousMonth] = [
        sortedMonths[sortedMonths.length - 1],
        sortedMonths[sortedMonths.length - 2],
    ];
    if (!Top3[currentMonth]) {
        return;
    }
    const totalLastMonth = Top3[currentMonth] + (RestTo100[currentMonth] ?? 0);
    const top3KeywordsPercentage = calculatePercentage(Top3[currentMonth] ?? 0, totalLastMonth);
    const change = !Top3[previousMonth]
        ? null
        : calculateChangeFromLastMonth({
              previousMonthValue: Top3[previousMonth],
              currentMonthValue: Top3[currentMonth],
          }) / 100;

    return {
        change,
        percentageOf: {
            formattedValue: `${top3KeywordsPercentage}${PERCENTAGE}`,
            numericValue: top3KeywordsPercentage,
        },
        keywords: roundNumberFilter()(Top3[currentMonth]),
    };
};

export const getPpcHeight = () => {
    const numOfSites = chosenSites.count();
    const heightMap: { [key: number]: string } = {
        2: "164px",
        3: "204px",
        4: "244px",
        5: "284px",
    };
    return heightMap[numOfSites] || "100%";
};
export const getMainDomain = (): string => {
    const params = proNavigator.getParams();
    const { key } = params;
    return (key as string).split(",")[0];
};
export const sortByDomainOrder = <T>(data: T[], getDomainName: (item: T) => string): T[] => {
    const orderedDomains = chosenSitesService.get();
    if (!Array.isArray(orderedDomains) || orderedDomains.length === 0) {
        return data;
    }
    const domainOrderMap = new Map(orderedDomains.map((domain, index) => [domain, index]));

    return [...data].sort((a, b) => {
        const aIndex = domainOrderMap.get(getDomainName(a)) ?? Infinity;
        const bIndex = domainOrderMap.get(getDomainName(b)) ?? Infinity;
        return aIndex - bIndex;
    });
};
